import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface Deal {
  id: string;
  title: string;
  subtitle: string;
  product_ids: number[];
  badge_filter: string;
  discount_percent: number;
  start_at: string;
  end_at: string;
  is_active: boolean;
  banner_color: string;
  created_at: string;
}

export function useDeals() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) setDeals(data as Deal[]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const addDeal = useCallback(async (deal: Omit<Deal, 'id' | 'created_at'>) => {
    const { error } = await supabase.from('deals').insert([deal]);
    if (!error) await fetchDeals();
    return !error;
  }, [fetchDeals]);

  const updateDeal = useCallback(async (id: string, deal: Partial<Deal>) => {
    const { error } = await supabase.from('deals').update(deal).eq('id', id);
    if (!error) await fetchDeals();
    return !error;
  }, [fetchDeals]);

  const deleteDeal = useCallback(async (id: string) => {
    const { error } = await supabase.from('deals').delete().eq('id', id);
    if (!error) await fetchDeals();
    return !error;
  }, [fetchDeals]);

  const activeDeals = deals.filter(d => {
    if (!d.is_active) return false;
    const now = new Date();
    return new Date(d.start_at) <= now && new Date(d.end_at) >= now;
  });

  return { deals, activeDeals, loading, addDeal, updateDeal, deleteDeal, refetch: fetchDeals };
}
